import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import {
  BUSINESS_GOAL_OPTIONS_BY_CATEGORY,
  CHIP_OPTIONS_BY_CATEGORY,
  MAX_INPUT_LENGTH,
  REVIEW_CATEGORIES,
  sanitizeInputText,
} from './constants';
import SlashMenu from './SlashMenu';

import styles from './styles.module.scss';

const ProductForm = ({
  productDescription,
  setProductDescription,
  title,
  setTitle,
  audience,
  setAudience,
  businessGoal,
  setBusinessGoal,
  mode = 'form',
  reviewData,
  selectedCategory,
  selectedProblemChips,
  setSelectedProblemChips,
}) => {
  const isDisplayMode = useMemo(() => mode === 'display', [mode]);
  const hasExistingReview = useMemo(() => (
    reviewData
    && typeof reviewData === 'object'
    && Object.keys(reviewData).length > 0
  ), [reviewData]);
  const shouldShowFullForm = !hasExistingReview;

  const displayTitle = useMemo(
    () => reviewData?.title || title || 'No title provided',
    [reviewData?.title, title]
  );

  const displayProduct = useMemo(
    () => reviewData?.extra_data?.user_problem || productDescription || 'No product description provided',
    [reviewData?.extra_data, productDescription]
  );

  const displayAudience = useMemo(
    () => reviewData?.extra_data?.audience || audience || 'No audience provided',
    [reviewData?.extra_data, audience]
  );

  const displayBusinessGoal = useMemo(
    () => reviewData?.extra_data?.business_goal || businessGoal || 'No business goal provided',
    [reviewData?.extra_data, businessGoal]
  );

  const displayCategory = useMemo(() => {
    const auditCategory = reviewData?.extra_data?.template || reviewData?.extra_data?.template;

    return auditCategory || selectedCategory;
  }, [reviewData?.extra_data, selectedCategory]);

  const activeCategory = displayCategory || REVIEW_CATEGORIES[0].key;

  const chipOptions = useMemo(
    () => CHIP_OPTIONS_BY_CATEGORY[activeCategory] || CHIP_OPTIONS_BY_CATEGORY.ux_critique,
    [activeCategory]
  );

  const businessGoalChipOptions = useMemo(
    () => BUSINESS_GOAL_OPTIONS_BY_CATEGORY[activeCategory] || BUSINESS_GOAL_OPTIONS_BY_CATEGORY.ux_critique,
    [activeCategory]
  );

  // Slash menu state
  const [problemDropdownOpen, setProblemDropdownOpen] = useState(false);
  const [problemHighlightedIndex, setProblemHighlightedIndex] = useState(-1);
  const [businessGoalDropdownOpen, setBusinessGoalDropdownOpen] = useState(false);
  const [businessGoalHighlightedIndex, setBusinessGoalHighlightedIndex] = useState(-1);

  // Preview text shown in textarea while hovering/navigating dropdown
  const problemPreviewText = problemDropdownOpen && problemHighlightedIndex >= 0
    ? chipOptions[problemHighlightedIndex].text
    : null;

  const businessGoalPreviewText = businessGoalDropdownOpen && businessGoalHighlightedIndex >= 0
    ? businessGoalChipOptions[businessGoalHighlightedIndex].text
    : null;

  // Problem handlers
  const handleSelectProblemTemplate = option => {
    setProductDescription(sanitizeInputText(option.text));
    setSelectedProblemChips([option.label]);
    setProblemDropdownOpen(false);
    setProblemHighlightedIndex(-1);
  };

  const closeProblemDropdown = () => {
    setProblemDropdownOpen(false);
    setProblemHighlightedIndex(-1);

    if (productDescription === '/') {
      setProductDescription('');

      if (Array.isArray(selectedProblemChips) && selectedProblemChips.length > 0) {
        setSelectedProblemChips([]);
      }
    }
  };

  const handleProblemChange = event => {
    if (isDisplayMode) {
      return;
    }

    const sanitized = sanitizeInputText(event.target.value);

    setProductDescription(sanitized);

    if (sanitized === '/') {
      setProblemDropdownOpen(true);
      setProblemHighlightedIndex(-1);
    }
    else if (problemDropdownOpen) {
      setProblemDropdownOpen(false);
      setProblemHighlightedIndex(-1);
    }

    if (Array.isArray(selectedProblemChips) && selectedProblemChips.length > 0) {
      setSelectedProblemChips([]);
    }
  };

  const handleProblemKeyDown = e => {
    if (!problemDropdownOpen) {
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setProblemHighlightedIndex(prev => Math.min(prev + 1, chipOptions.length - 1));
    }
    else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setProblemHighlightedIndex(prev => Math.max(prev - 1, -1));
    }
    else if (e.key === 'Enter') {
      e.preventDefault();

      if (problemHighlightedIndex >= 0) {
        handleSelectProblemTemplate(chipOptions[problemHighlightedIndex]);
      }
    }
    else if (e.key === 'Escape') {
      closeProblemDropdown();
    }
  };

  // Business goal handlers
  const handleSelectBusinessGoalTemplate = option => {
    setBusinessGoal(sanitizeInputText(option.text));
    setBusinessGoalDropdownOpen(false);
    setBusinessGoalHighlightedIndex(-1);
  };

  const closeBusinessGoalDropdown = () => {
    setBusinessGoalDropdownOpen(false);
    setBusinessGoalHighlightedIndex(-1);

    if (businessGoal === '/') {
      setBusinessGoal('');
    }
  };

  const handleBusinessGoalChange = event => {
    if (isDisplayMode) {
      return;
    }

    const sanitized = sanitizeInputText(event.target.value);

    setBusinessGoal(sanitized);

    if (sanitized === '/') {
      setBusinessGoalDropdownOpen(true);
      setBusinessGoalHighlightedIndex(-1);
    }
    else if (businessGoalDropdownOpen) {
      setBusinessGoalDropdownOpen(false);
      setBusinessGoalHighlightedIndex(-1);
    }
  };

  const handleBusinessGoalKeyDown = e => {
    if (!businessGoalDropdownOpen) {
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setBusinessGoalHighlightedIndex(prev => Math.min(prev + 1, businessGoalChipOptions.length - 1));
    }
    else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setBusinessGoalHighlightedIndex(prev => Math.max(prev - 1, -1));
    }
    else if (e.key === 'Enter') {
      e.preventDefault();

      if (businessGoalHighlightedIndex >= 0) {
        handleSelectBusinessGoalTemplate(businessGoalChipOptions[businessGoalHighlightedIndex]);
      }
    }
    else if (e.key === 'Escape') {
      closeBusinessGoalDropdown();
    }
  };

  const handleAudienceChange = event => {
    if (isDisplayMode) {
      return;
    }

    const sanitizedValue = sanitizeInputText(event.target.value);

    setAudience(sanitizedValue);
  };

  useEffect(() => {
    if (isDisplayMode) {
      return;
    }

    if (typeof productDescription === 'string') {
      const sanitized = sanitizeInputText(productDescription);

      if (sanitized !== productDescription) {
        setProductDescription(sanitized);
      }
    }

    if (typeof audience === 'string') {
      const sanitized = sanitizeInputText(audience);

      if (sanitized !== audience) {
        setAudience(sanitized);
      }
    }

    if (typeof businessGoal === 'string') {
      const sanitized = sanitizeInputText(businessGoal);

      if (sanitized !== businessGoal) {
        setBusinessGoal(sanitized);
      }
    }
  }, [isDisplayMode, productDescription, setProductDescription, audience, setAudience, businessGoal, setBusinessGoal]);

  // Computed display values
  const displayedAudienceValue = isDisplayMode
    ? (displayAudience || '')
    : sanitizeInputText(audience);

  const displayedBusinessGoalValue = isDisplayMode
    ? (displayBusinessGoal || '')
    : sanitizeInputText(businessGoal);

  const problemTextareaValue = isDisplayMode
    ? (displayProduct || '')
    : (problemPreviewText !== null ? problemPreviewText : sanitizeInputText(productDescription));

  const businessGoalTextareaValue = isDisplayMode
    ? displayedBusinessGoalValue
    : (businessGoalPreviewText !== null ? businessGoalPreviewText : sanitizeInputText(businessGoal));

  // Refs for auto-growing textareas in display mode
  const audienceTextareaRef = useRef(null);
  const problemTextareaRef = useRef(null);
  const businessGoalTextareaRef = useRef(null);

  // Expand textarea to fit all content for display mode
  const expandTextareaToContent = textarea => {
    if (!textarea || !isDisplayMode) {
      return;
    }

    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  // Expand textareas in display mode when content loads
  useEffect(() => {
    if (isDisplayMode) {
      // Small delay to ensure DOM is updated with content
      const timer = setTimeout(() => {
        if (audienceTextareaRef.current) {
          expandTextareaToContent(audienceTextareaRef.current);
        }
        if (problemTextareaRef.current) {
          expandTextareaToContent(problemTextareaRef.current);
        }
        if (businessGoalTextareaRef.current) {
          expandTextareaToContent(businessGoalTextareaRef.current);
        }
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [isDisplayMode, displayedAudienceValue, problemTextareaValue, displayedBusinessGoalValue]);

  return (
    <div
      className={cn('flex flex-col gap-10 2xl:gap-12 overflow-auto pb-0', {
        'p-0': isDisplayMode,
        'p-7': !isDisplayMode,
      })}
    >
      {shouldShowFullForm && !isDisplayMode && (
        <div className={styles['context-heading']}>
          <h2 className={styles['context-heading__title']}>
            Add your context for better results
          </h2>
          <span className="text-gray-400 ml-auto">
            *
            {' '}
            Required
          </span>
        </div>
      )}

      <div
        className={cn(
          'flex flex-col gap-10 2xl:gap-12 pr-8 pl-1',
          {
            'overflow-y-auto': isDisplayMode,
          }
        )}
      >
        {shouldShowFullForm && (
          <>
            <label className="text-base text-gray-900 h-5">
              Title
              {' '}
              <span className="text-gray-400">
                *
              </span>
            </label>
            <input
              value={isDisplayMode ? displayTitle : title}
              onChange={e => setTitle(e.target.value)}
              placeholder={'Name of review'}
              readOnly={isDisplayMode}
              className={cn(
                'w-full rounded-lg text-sm resize-none focus:outline-none p-3',
                '2xl:mb-3 mb-5',
                {
                  'bg-gray-50 text-gray-500 border-0': isDisplayMode,
                  'border border-gray-300 text-gray-600 placeholder-gray-500': !isDisplayMode,
                  'focus:ring-2 focus:ring-primary-400 focus:border-transparent': !isDisplayMode,
                }
              )}
            />
          </>
        )}

        {(shouldShowFullForm || displayedAudienceValue) && (
          <>
            <label className="text-base font-medium text-gray-900 h-5">
              Audience
            </label>
            <textarea
              ref={audienceTextareaRef}
              value={displayedAudienceValue}
              onChange={handleAudienceChange}
              placeholder="Who needs this feature"
              readOnly={isDisplayMode}
              maxLength={MAX_INPUT_LENGTH}
              className={cn(
                'w-full rounded-lg text-sm resize-none focus:outline-none p-3',
                {
                  'bg-gray-50 text-gray-500 border-0 mb-5 min-h-auto': isDisplayMode,
                  'border border-gray-300 text-gray-600 placeholder-gray-500 min-h-24 overflow-y-auto': !isDisplayMode,
                  'focus:ring-2 focus:ring-primary-400 focus:border-transparent': !isDisplayMode,
                  'max-h-28': !isDisplayMode,
                }
              )}
            />
            {!isDisplayMode && (
              <div className={cn(styles['problem-meta'], ' mb-5')}>
                <span
                  className={styles['char-counter']}
                  style={{
                    color: MAX_INPUT_LENGTH - displayedAudienceValue.length === 0 ? '#ef4444' : '#94a3b8',
                  }}
                >
                  {MAX_INPUT_LENGTH - displayedAudienceValue.length}
                </span>
              </div>
            )}
          </>
        )}

        <label className="text-base text-gray-900 h-5">
          Problem to solve
        </label>
        <div className="relative">
          {!isDisplayMode && problemDropdownOpen && (
            <SlashMenu
              options={chipOptions}
              highlightedIndex={problemHighlightedIndex}
              onHover={setProblemHighlightedIndex}
              onSelect={handleSelectProblemTemplate}
            />
          )}
          <textarea
            ref={problemTextareaRef}
            value={problemTextareaValue}
            onChange={handleProblemChange}
            onKeyDown={handleProblemKeyDown}
            onBlur={closeProblemDropdown}
            placeholder="Type / for templates"
            readOnly={isDisplayMode || problemPreviewText !== null}
            maxLength={MAX_INPUT_LENGTH}
            className={cn(
              'w-full rounded-lg text-sm resize-none focus:outline-none p-3',
              {
                'bg-gray-50 text-gray-500 border-0 mb-5 min-h-auto overflow-hidden': isDisplayMode,
                'border border-gray-300 text-gray-600 placeholder-gray-500 min-h-24 overflow-y-auto': !isDisplayMode,
                'focus:ring-2 focus:ring-primary-400 focus:border-transparent': !isDisplayMode,
                'max-h-28 h-1/2 2xl:h-16': !isDisplayMode,
                [styles['textarea--preview']]: problemPreviewText !== null,
              }
            )}
          />
        </div>

        {!isDisplayMode && (
          <div className={cn(styles['problem-meta'], 'mb-5')}>
            <span
              className={styles['char-counter']}
              style={{
                color: MAX_INPUT_LENGTH - sanitizeInputText(productDescription).length === 0 ? '#ef4444' : '#94a3b8',
              }}
            >
              {MAX_INPUT_LENGTH - sanitizeInputText(productDescription).length}
            </span>
          </div>
        )}

        {(shouldShowFullForm || displayedBusinessGoalValue) && (
          <>
            <label className="text-base text-gray-900 h-5">
              Business goal
            </label>
            <div className="relative">
              {!isDisplayMode && businessGoalDropdownOpen && (
                <SlashMenu
                  options={businessGoalChipOptions}
                  highlightedIndex={businessGoalHighlightedIndex}
                  onHover={setBusinessGoalHighlightedIndex}
                  onSelect={handleSelectBusinessGoalTemplate}
                />
              )}
              <textarea
                ref={businessGoalTextareaRef}
                value={businessGoalTextareaValue}
                onChange={handleBusinessGoalChange}
                onKeyDown={handleBusinessGoalKeyDown}
                onBlur={closeBusinessGoalDropdown}
                placeholder="Type / for templates"
                readOnly={isDisplayMode || businessGoalPreviewText !== null}
                maxLength={MAX_INPUT_LENGTH}
                className={cn(
                  'w-full rounded-lg text-sm resize-none focus:outline-none p-3',
                  {
                    'bg-gray-50 text-gray-500 border-0 mb-5 min-h-auto overflow-hidden': isDisplayMode,
                    'border border-gray-300 text-gray-600 placeholder-gray-500': !isDisplayMode,
                    'min-h-24 overflow-y-auto max-h-28': !isDisplayMode,
                    'focus:ring-2 focus:ring-primary-400 focus:border-transparent': !isDisplayMode,
                    [styles['textarea--preview']]: businessGoalPreviewText !== null,
                  }
                )}
              />
            </div>
            {!isDisplayMode && (
              <div className={cn('mb-5', styles['problem-meta'])}>
                <span
                  className={styles['char-counter']}
                  style={{
                    color: MAX_INPUT_LENGTH - sanitizeInputText(businessGoal).length === 0 ? '#ef4444' : '#94a3b8',
                  }}
                >
                  {MAX_INPUT_LENGTH - sanitizeInputText(businessGoal).length}
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

ProductForm.propTypes = {
  productDescription: PropTypes.string,
  setProductDescription: PropTypes.func,
  title: PropTypes.string,
  setTitle: PropTypes.func,
  audience: PropTypes.string,
  setAudience: PropTypes.func,
  businessGoal: PropTypes.string,
  setBusinessGoal: PropTypes.func,
  mode: PropTypes.oneOf(['form', 'display']),
  reviewData: PropTypes.shape({
    title: PropTypes.string,
    extra_data: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  }),
  selectedCategory: PropTypes.string,
  selectedProblemChips: PropTypes.arrayOf(PropTypes.string),
  setSelectedProblemChips: PropTypes.func,
};

ProductForm.defaultProps = {
  productDescription: '',
  setProductDescription: () => {},
  title: '',
  setTitle: () => {},
  audience: '',
  setAudience: () => {},
  businessGoal: '',
  setBusinessGoal: () => {},
  mode: 'form',
  reviewData: {},
  selectedCategory: REVIEW_CATEGORIES[0].key,
  selectedProblemChips: [],
  setSelectedProblemChips: () => {},
};

export default ProductForm;
